import React, { useState, useRef, useCallback, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import {
  LANG_STYLE,
  detectLang,
  registerEditorAssets,
  clearEditorMarkers,
  EDITOR_BASE_OPTIONS,
} from './codeEditorShared';

const MIN_W = 380;
const MIN_H = 260;

const FloatingCodeEditor = ({
  id,
  shapeId,
  shapeLabel,
  initialCode,
  initialPrompt,        // AI prompt başlangıç değeri
  onCodeChange,         // (shapeId, newCode) => void
  onPromptChange,       // (shapeId, newPrompt) => void
  onClose,              // () => void
  onDock,               // () => void  — paneli tekrar dock'a al
  saveScript,           // (shapeId, code) => Promise
  defaultX = 80,
  defaultY = 60,
  defaultW = 640,
  defaultH = 480,
}) => {
  const [code, setCode] = useState(initialCode || '');
  const [prompt, setPrompt] = useState(initialPrompt || '');
  const [showPromptPanel, setShowPromptPanel] = useState(false);
  const [promptPanelHeight, setPromptPanelHeight] = useState(28); // %
  const [isResizingPrompt, setIsResizingPrompt] = useState(false);
  const [pos, setPos] = useState({ x: defaultX, y: defaultY });
  const [size, setSize] = useState({ w: defaultW, h: defaultH });
  const [minimized, setMinimized] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [zIndex, setZIndex] = useState(900000);
  const promptResizeStartY = useRef(0);
  const promptResizeStartH = useRef(0);
  const promptContainerH = useRef(0);

  const dragging = useRef(null);
  const resizing = useRef(null);
  const editorRef = useRef(null);
  const panelRef = useRef(null);

  const lang = detectLang(shapeLabel);
  const langStyle = LANG_STYLE[lang] || LANG_STYLE.php;
  const langLabel = lang.toUpperCase().replace('JAVASCRIPT', 'JS').replace('TYPESCRIPT', 'TS');

  /* ── Öne getir ── */
  const bringToFront = useCallback(() => {
    setZIndex((z) => z + 1);
  }, []);

  /* ── Sürükleme ── */
  const handleDragStart = useCallback((e) => {
    if (e.target.closest('button') || minimized === false && e.target.closest('.resize-handle')) return;
    e.preventDefault();
    bringToFront();
    dragging.current = { startX: e.clientX - pos.x, startY: e.clientY - pos.y };

    const onMove = (ev) => {
      if (!dragging.current) return;
      setPos({
        x: Math.max(0, ev.clientX - dragging.current.startX),
        y: Math.max(0, ev.clientY - dragging.current.startY),
      });
    };
    const onUp = () => {
      dragging.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [pos, bringToFront, minimized]);

  /* ── Yeniden boyutlandırma ── */
  const handleResizeStart = useCallback((e, dir) => {
    e.preventDefault();
    e.stopPropagation();
    bringToFront();
    resizing.current = {
      dir,
      startX: e.clientX, startY: e.clientY,
      startW: size.w, startH: size.h,
      startPosX: pos.x, startPosY: pos.y,
    };

    const onMove = (ev) => {
      const r = resizing.current;
      if (!r) return;
      const dx = ev.clientX - r.startX;
      const dy = ev.clientY - r.startY;

      setSize((s) => {
        let w = s.w;
        let h = s.h;
        if (dir.includes('e')) w = Math.max(MIN_W, r.startW + dx);
        if (dir.includes('w')) w = Math.max(MIN_W, r.startW - dx);
        if (dir.includes('s')) h = Math.max(MIN_H, r.startH + dy);
        if (dir.includes('n')) h = Math.max(MIN_H, r.startH - dy);
        return { w, h };
      });

      if (dir.includes('w')) {
        setPos((p) => ({ ...p, x: Math.max(0, r.startPosX + dx) }));
      }
      if (dir.includes('n')) {
        setPos((p) => ({ ...p, y: Math.max(0, r.startPosY + dy) }));
      }
    };
    const onUp = () => {
      resizing.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [size, pos, bringToFront]);

  /* ── Monaco ── */
  const handleBeforeMount = useCallback((monaco) => {
    registerEditorAssets(monaco);
  }, []);

  const handleEditorMount = useCallback((editor, monaco) => {
    editorRef.current = editor;
    clearEditorMarkers(editor, monaco);
    editor.addCommand(
      monaco.KeyMod.Shift | monaco.KeyMod.Alt | monaco.KeyCode.KeyF,
      () => editor.getAction('editor.action.formatDocument')?.run(),
    );
  }, []);

  const handleChange = useCallback((value) => {
    const v = value || '';
    setCode(v);
    onCodeChange?.(shapeId, v);
  }, [shapeId, onCodeChange]);

  const handleFormat = useCallback(() => {
    editorRef.current?.getAction('editor.action.formatDocument')?.run();
  }, []);

  /* ── Prompt panel dikey resize ── */
  const startPromptResize = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const container = e.currentTarget.parentElement;
    promptContainerH.current = container?.getBoundingClientRect().height || 400;
    promptResizeStartY.current = e.clientY;
    promptResizeStartH.current = promptPanelHeight;
    setIsResizingPrompt(true);

    const onMove = (ev) => {
      const dy = promptResizeStartY.current - ev.clientY;
      const pct = (dy / (promptContainerH.current || 400)) * 100;
      setPromptPanelHeight((h) => Math.min(60, Math.max(15, promptResizeStartH.current + pct)));
    };
    const onUp = () => {
      setIsResizingPrompt(false);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [promptPanelHeight]);

  /* ── Prompt değişikliği ── */
  const handlePromptChange = useCallback((value) => {
    setPrompt(value);
    onPromptChange?.(shapeId, value);
  }, [shapeId, onPromptChange]);

  /* ── Kaydet ── */
  const handleSave = useCallback(async () => {
    if (!saveScript || !shapeId) return;
    setIsSaving(true);
    setSaveMsg(null);
    try {
      const result = await saveScript(shapeId, code);
      setSaveMsg(result?.success ? 'success' : 'error');
    } catch {
      setSaveMsg('error');
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveMsg(null), 2500);
    }
  }, [saveScript, shapeId, code]);

  /* ── Layout ── */
  const HEADER_H = 38;
  const TOOLBAR_H = 32;
  const editorH = size.h - HEADER_H - TOOLBAR_H;

  return (
    <div
      ref={panelRef}
      onMouseDown={bringToFront}
      style={{
        position: 'fixed',
        left: pos.x, top: pos.y,
        width: size.w,
        height: minimized ? HEADER_H : size.h,
        zIndex,
        display: 'flex', flexDirection: 'column',
        borderRadius: 10,
        overflow: 'hidden',
        boxShadow: '0 24px 64px rgba(0,0,0,0.7), 0 0 0 1px #21262d',
        background: '#0d1117',
        fontFamily: "'Segoe UI', system-ui, sans-serif",
        userSelect: dragging.current || resizing.current ? 'none' : 'auto',
      }}
    >
      {/* ── Header / drag handle ── */}
      <div
        onMouseDown={handleDragStart}
        style={{
          height: HEADER_H,
          background: '#010409',
          borderBottom: `1px solid ${minimized ? 'transparent' : '#21262d'}`,
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '0 8px 0 12px',
          cursor: 'move', flexShrink: 0,
          userSelect: 'none',
        }}
      >
        {/* Colored dot */}
        <span style={{
          width: 8, height: 8, borderRadius: '50%',
          background: langStyle.color, flexShrink: 0,
        }} />

        {/* Title */}
        <span style={{
          flex: 1, minWidth: 0,
          color: '#cdd9e5', fontSize: 12, fontWeight: 500,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {shapeLabel || shapeId}
        </span>

        {/* Lang badge */}
        <span style={{
          fontSize: 9.5, fontWeight: 700, letterSpacing: '0.05em',
          color: langStyle.color, background: langStyle.bg,
          border: `1px solid ${langStyle.border}`,
          borderRadius: 4, padding: '2px 5px', flexShrink: 0,
        }}>
          {langLabel}
        </span>

        {/* Yüzer panel ikonu */}
        <span style={{ fontSize: 9, color: '#484f58', flexShrink: 0, userSelect: 'none' }}>⎋ Float</span>

        {/* Minimize */}
        <button
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => setMinimized((m) => !m)}
          title={minimized ? 'Geri Aç' : 'Küçült'}
          style={{
            background: 'none', border: 'none', color: '#484f58',
            cursor: 'pointer', padding: '3px 5px', lineHeight: 1, fontSize: 13, flexShrink: 0,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#fbbf24'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = '#484f58'; }}
        >
          {minimized ? '▲' : '▼'}
        </button>

        {/* Dock */}
        {onDock && (
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={onDock}
            title="Panele sabitle"
            style={{
              background: 'none', border: 'none', color: '#484f58',
              cursor: 'pointer', padding: '3px 5px', lineHeight: 1, fontSize: 12, flexShrink: 0,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#79c0ff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#484f58'; }}
          >
            ⬟
          </button>
        )}

        {/* Close */}
        <button
          onMouseDown={(e) => e.stopPropagation()}
          onClick={onClose}
          title="Kapat"
          style={{
            background: 'none', border: 'none', color: '#484f58',
            cursor: 'pointer', padding: '3px 5px', lineHeight: 1, fontSize: 16, flexShrink: 0,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#ff7b72'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = '#484f58'; }}
        >
          ×
        </button>
      </div>

      {!minimized && (
        <>
          {/* ── Toolbar ── */}
          <div style={{
            height: TOOLBAR_H, background: '#010409',
            borderBottom: '1px solid #21262d',
            display: 'flex', alignItems: 'center',
            padding: '0 8px', gap: 6, flexShrink: 0,
          }}>
            {/* Format */}
            <button
              onClick={handleFormat}
              title="Format (Shift+Alt+F)"
              style={{
                background: 'transparent', border: '1px solid #21262d', color: '#8b949e',
                padding: '2px 8px', borderRadius: 4, fontSize: 11, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 4,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#388bfd'; e.currentTarget.style.color = '#79c0ff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#21262d'; e.currentTarget.style.color = '#8b949e'; }}
            >
              <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <line x1="2" y1="4" x2="14" y2="4" />
                <line x1="2" y1="8" x2="10" y2="8" />
                <line x1="2" y1="12" x2="12" y2="12" />
                <polyline points="12 6 14 8 12 10" />
              </svg>
              Format
            </button>

            {/* Kaydet */}
            <button
              onClick={handleSave}
              disabled={isSaving}
              title="Kaydet (Ctrl+S)"
              style={{
                background: isSaving ? '#161b22' : 'linear-gradient(135deg,#1a7f37,#238636)',
                border: '1px solid #2ea04330', color: '#fff',
                padding: '2px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                cursor: isSaving ? 'not-allowed' : 'pointer',
              }}
            >
              {isSaving ? '...' : 'Kaydet'}
            </button>

            {saveMsg && (
              <span style={{
                fontSize: 10, fontWeight: 500,
                color: saveMsg === 'success' ? '#7ee787' : '#ff7b72',
              }}>
                {saveMsg === 'success' ? '✓ Kaydedildi' : '✗ Hata'}
              </span>
            )}

            {/* Spacer */}
            <div style={{ flex: 1 }} />

            {/* Agent butonu */}
            <button
              onClick={() => setShowPromptPanel((v) => !v)}
              title="AI Agent Paneli"
              style={{
                background: showPromptPanel ? 'linear-gradient(135deg,#3730a3,#6d28d9)' : 'transparent',
                border: `1px solid ${showPromptPanel ? '#6d28d980' : '#30363d'}`,
                color: showPromptPanel ? '#fff' : '#8b949e',
                padding: '2px 8px', borderRadius: 4, fontSize: 11,
                cursor: 'pointer', fontWeight: 500,
                display: 'flex', alignItems: 'center', gap: 4,
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => { if (!showPromptPanel) { e.currentTarget.style.borderColor = '#6d28d9'; e.currentTarget.style.color = '#c4b5fd'; } }}
              onMouseLeave={(e) => { if (!showPromptPanel) { e.currentTarget.style.borderColor = '#30363d'; e.currentTarget.style.color = '#8b949e'; } }}
            >
              <span style={{ fontSize: 11 }}>✦</span>
              Agent
              <span style={{ fontSize: 8, opacity: 0.7 }}>{showPromptPanel ? '▼' : '▶'}</span>
            </button>
          </div>

          {/* ── Monaco Editor ── */}
          <div style={{
            flex: showPromptPanel ? `1 1 ${100 - promptPanelHeight}%` : '1',
            minHeight: 100, overflow: 'hidden',
          }}>
            <Editor
              height="100%" width="100%"
              language={lang} value={code}
              theme="cursor-dark"
              onChange={handleChange}
              onMount={handleEditorMount}
              beforeMount={handleBeforeMount}
              key={`float-editor-${id}`}
              options={{ ...EDITOR_BASE_OPTIONS, minimap: { enabled: false } }}
            />
          </div>

          {/* ── Vertical Resize Handle (prompt) ── */}
          {showPromptPanel && (
            <div
              onMouseDown={startPromptResize}
              style={{
                height: 5,
                background: isResizingPrompt ? '#388bfd' : '#21262d',
                cursor: 'row-resize', flexShrink: 0, transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => { if (!isResizingPrompt) e.currentTarget.style.background = '#388bfd'; }}
              onMouseLeave={(e) => { if (!isResizingPrompt) e.currentTarget.style.background = '#21262d'; }}
            />
          )}

          {/* ── AI Prompt Paneli ── */}
          {showPromptPanel && (
            <div style={{
              flex: `0 0 ${promptPanelHeight}%`,
              minHeight: 100, maxHeight: '60%',
              display: 'flex', flexDirection: 'column',
              background: '#0d1117', borderTop: '1px solid #21262d',
            }}>
              {/* Prompt Header */}
              <div style={{
                background: '#010409', padding: '5px 12px',
                borderBottom: '1px solid #21262d',
                display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
              }}>
                <span style={{ fontSize: 11, color: '#a78bfa' }}>✦</span>
                <span style={{ color: '#cdd9e5', fontSize: 11, fontWeight: 600 }}>AI Prompt</span>
                <span style={{ fontSize: 9.5, color: '#484f58', fontStyle: 'italic' }}>
                  · Kaydet butonuyla sunucuya gönderilir
                </span>
              </div>
              {/* Textarea */}
              <div style={{ flex: 1, minHeight: 0, padding: '8px 10px' }}>
                <textarea
                  value={prompt}
                  onChange={(e) => handlePromptChange(e.target.value)}
                  placeholder="AI talimatınızı buraya yazın..."
                  style={{
                    width: '100%', height: '100%',
                    background: '#161b22', border: '1px solid #21262d', borderRadius: 5,
                    color: '#e6edf3', fontSize: 12,
                    fontFamily: "'Cascadia Code','Fira Code','JetBrains Mono',Consolas,monospace",
                    lineHeight: 1.6, padding: '8px 10px', resize: 'none',
                    outline: 'none', boxSizing: 'border-box', caretColor: '#79c0ff',
                    transition: 'border-color 0.15s',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = '#388bfd'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#21262d'; }}
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Resize handles (sadece büyütülmüş modda) ── */}
      {!minimized && (
        <>
          {/* E */}
          <div className="resize-handle" onMouseDown={(e) => handleResizeStart(e, 'e')} style={{
            position: 'absolute', right: 0, top: 24, bottom: 24, width: 6,
            cursor: 'ew-resize', zIndex: 2,
          }} />
          {/* W */}
          <div className="resize-handle" onMouseDown={(e) => handleResizeStart(e, 'w')} style={{
            position: 'absolute', left: 0, top: 24, bottom: 24, width: 6,
            cursor: 'ew-resize', zIndex: 2,
          }} />
          {/* S */}
          <div className="resize-handle" onMouseDown={(e) => handleResizeStart(e, 's')} style={{
            position: 'absolute', bottom: 0, left: 24, right: 24, height: 6,
            cursor: 'ns-resize', zIndex: 2,
          }} />
          {/* SE */}
          <div className="resize-handle" onMouseDown={(e) => handleResizeStart(e, 'se')} style={{
            position: 'absolute', right: 0, bottom: 0, width: 14, height: 14,
            cursor: 'nwse-resize', zIndex: 3,
            background: 'rgba(255,255,255,0.06)', borderRadius: '0 0 10px 0',
          }} />
          {/* SW */}
          <div className="resize-handle" onMouseDown={(e) => handleResizeStart(e, 'sw')} style={{
            position: 'absolute', left: 0, bottom: 0, width: 14, height: 14,
            cursor: 'nesw-resize', zIndex: 3,
            background: 'rgba(255,255,255,0.06)', borderRadius: '0 0 0 10px',
          }} />
          {/* N */}
          <div className="resize-handle" onMouseDown={(e) => handleResizeStart(e, 'n')} style={{
            position: 'absolute', top: HEADER_H, left: 24, right: 24, height: 5,
            cursor: 'ns-resize', zIndex: 2,
          }} />
        </>
      )}
    </div>
  );
};

export default FloatingCodeEditor;
